/**
 * Minting Dapp Framework - TypeScript Interfaces
 *
 * Defines the core types for the plugin-based minting dapp system.
 * Supports both internal (React components) and external (iframe) dapps.
 *
 * @module minting/types
 * @category Minting Framework
 */

/**
 * Represents the wallet/blockchain state passed to minting dapps
 */
export interface WalletState {
  address: string | null;
  chainId: number | null;
  isConnected: boolean;
}

/**
 * Props passed to internal minting dapp components
 */
export interface MintingDappProps {
  dappId: string;
  dappName: string;
  appKit: any; // AppKit instance - typed as any to avoid dependency on @reown/appkit types
  account?: string;
  chainId?: number;
  isConnected?: boolean;
  walletState?: WalletState;
  // Communication: for internal dapps that want to send messages to parent
  sendMessage?: (message: PostMessageEvent) => void;
  onMessage?: (message: PostMessageEvent) => void;
}

/**
 * Configuration for a minting dapp
 */
export interface MintingDappConfig {
  allowedOrigins?: string[];  // for external security - allowed iframe origins
  sandbox?: string;          // iframe sandbox attributes (for external dapps)
  allowedChains?: number[];  // which chains this dapp supports
  requireAuth?: boolean;     // whether wallet connection is required
}

/**
 * Core Minting Dapp interface
 * Can be either internal (React component) or external (iframe URL)
 */
export interface MintingDapp {
  id: string;
  name: string;
  description?: string;
  type: 'internal' | 'external';
  // For internal dapps:
  component?: React.ComponentType<MintingDappProps>;
  // For external dapps:
  url?: string;
  // Configuration
  config?: MintingDappConfig;
  // Metadata
  version?: string;
  author?: string;
  icon?: string; // URL to icon image
  tags?: string[];
  // Lifecycle hooks
  onInit?: (dappId: string, props: MintingDappProps) => void;
  onDestroy?: (dappId: string) => void;
}

/**
 * Base structure for all postMessage communications
 * Used for both parent->child and child->parent messaging
 */
export interface PostMessageEvent {
  type: string;
  payload: any;
  sender: 'parent' | 'child';
  timestamp: number;
}

/**
 * PostMessage protocol message types
 * Defines all supported message types for parent-iframe communication
 */
export enum MessageType {
  // Parent -> Child (iframe)
  APPKIT_INIT = 'APPKIT_INIT',
  APPKIT_UPDATE = 'APPKIT_UPDATE',
  APPKIT_DISCONNECT = 'APPKIT_DISCONNECT',
  PARENT_READY = 'PARENT_READY',

  // Child -> Parent
  REQUEST_CONNECT = 'REQUEST_CONNECT',
  REQUEST_SIGN_TX = 'REQUEST_SIGN_TX',
  REQUEST_SIGN_MESSAGE = 'REQUEST_SIGN_MESSAGE',
  REQUEST_SWITCH_CHAIN = 'REQUEST_SWITCH_CHAIN',
  REQUEST_ACCOUNT = 'REQUEST_ACCOUNT',
  CHILD_READY = 'CHILD_READY',

  // Responses
  APPKIT_STATE = 'APPKIT_STATE',
  TRANSACTION_RESPONSE = 'TRANSACTION_RESPONSE',
  MESSAGE_RESPONSE = 'MESSAGE_RESPONSE',
  CHAIN_SWITCH_RESPONSE = 'CHAIN_SWITCH_RESPONSE',
  ERROR = 'ERROR',

  // Analytics/events
  EVENT_LOG = 'EVENT_LOG',
}

/**
 * AppKit initialization message (Parent -> Child)
 */
export interface AppKitInitMessage {
  type: MessageType.APPKIT_INIT;
  payload: WalletState;
}

/**
 * AppKit state update message (Parent -> Child)
 */
export interface AppKitUpdateMessage {
  type: MessageType.APPKIT_UPDATE;
  payload: WalletState;
}

/**
 * Request from child for parent to sign a transaction
 */
export interface RequestSignTxMessage {
  type: MessageType.REQUEST_SIGN_TX;
  payload: {
    transaction: any;  // Ethers transaction object
    requestId: string;
    chainId?: number;
  };
}

/**
 * Request from child for parent to sign a message
 */
export interface RequestSignMessageMessage {
  type: MessageType.REQUEST_SIGN_MESSAGE;
  payload: {
    message: string | { types?: any; primaryType?: string; domain?: any; message?: any }; // EIP-712 or simple string
    requestId: string;
    address?: string;
  };
}

/**
 * Request from child for parent to switch chain
 */
export interface RequestSwitchChainMessage {
  type: MessageType.REQUEST_SWITCH_CHAIN;
  payload: {
    chainId: number;
    requestId: string;
  };
}

/**
 * Response to any request (tx sign, message sign, chain switch)
 */
export interface TransactionResponse {
  requestId: string;
  success: boolean;
  result?: any;
  error?: string;
}

/**
 * AppKit state response (sent by parent when child requests it)
 */
export interface AppKitStateMessage {
  type: MessageType.APPKIT_STATE;
  payload: WalletState;
}

/**
 * Error message
 */
export interface ErrorMessage {
  type: MessageType.ERROR;
  payload: {
    requestId?: string;
    error: string;
    code?: string | number;
  };
}

/**
 * Union type for all possible postMessage data
 */
export type PostMessageData =
  | AppKitInitMessage
  | AppKitUpdateMessage
  | RequestSignTxMessage
  | RequestSignMessageMessage
  | RequestSwitchChainMessage
  | TransactionResponse
  | AppKitStateMessage
  | ErrorMessage
  | { type: MessageType.PARENT_READY }
  | { type: MessageType.CHILD_READY }
  | { type: MessageType.EVENT_LOG; payload: { event: string; data?: any } };

/**
 * Type guard for PostMessageEvent
 */
export function isPostMessageEvent(data: any): data is PostMessageEvent {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof data.type === 'string' &&
    typeof data.payload !== 'undefined' &&
    (data.sender === 'parent' || data.sender === 'child') &&
    typeof data.timestamp === 'number'
  );
}

/**
 * Type guard for specific message types
 */
export function isMessageType<T extends MessageType>(
  data: any,
  type: T
): data is Extract<PostMessageData, { type: T }> {
  return data?.type === type;
}
